import React from "react";
import { Navigate, Route, Routes, useParams } from "react-router";
import Home from "src/Pages/Home";
import Posts from "src/Pages/Posts";
import BlogPost from "src/Pages/BlogPost";
import TermsAndPrivacy from "src/Pages/TermsServicePolicy";
import AudienceLanding from "src/Pages/AudienceLanding";
import Dashboard from "src/Pages/Dashboard";
import Pricing from "src/Pages/Pricing";
import { CitySeoPage, CountryHubPage } from "src/Pages/Markets";
import SignIn from "src/Pages/SignIn";
import RequireAuth from "src/Components/RequireAuth";
import Admin from "src/Pages/Admin";
import { ADMIN_PATH, DASHBOARD_PATH, LOGIN_PATH } from "src/constants/paths";
import { audienceFromSegment, AudienceSlug } from "src/i18n/paths";
import { isLocalePathPrefix } from "src/i18n/localesConfig";

const AudienceRoute: React.FC = () => {
  const { audienceSegment } = useParams<{ audienceSegment: string }>();
  const audience = audienceFromSegment(audienceSegment);
  if (!audience) {
    return <Navigate to="/" replace />;
  }
  return <AudienceLanding audience={audience as AudienceSlug} />;
};

const LocalePrefixGate: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { locale: localeParam } = useParams<{ locale: string }>();
  if (!isLocalePathPrefix(localeParam)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

export const AppRoutes: React.FC = () => (
  <Routes>
    <Route
      path={DASHBOARD_PATH}
      element={
        <RequireAuth>
          <Dashboard />
        </RequireAuth>
      }
    />
    <Route
      path={ADMIN_PATH}
      element={
        <RequireAuth adminOnly>
          <Admin />
        </RequireAuth>
      }
    />
    <Route path={LOGIN_PATH} element={<SignIn />} />

    <Route path="/" element={<Home />} />
    <Route path="/for/:audienceSegment" element={<AudienceRoute />} />
    <Route path="/para/:audienceSegment" element={<AudienceRoute />} />
    <Route path="/blog" element={<Posts />} />
    <Route path="/blog/:slug" element={<BlogPost />} />
    <Route path="/terms-privacy" element={<TermsAndPrivacy />} />
    <Route path="/pricing" element={<Pricing />} />
    <Route path="/precios" element={<Pricing />} />
    <Route path="/markets/:country" element={<CountryHubPage />} />
    <Route path="/markets/:country/:city" element={<CitySeoPage />} />
    <Route path="/service/one-time" element={<Navigate to="/" replace />} />

    <Route
      path="/:locale"
      element={
        <LocalePrefixGate>
          <Home />
        </LocalePrefixGate>
      }
    />
    <Route
      path="/:locale/for/:audienceSegment"
      element={
        <LocalePrefixGate>
          <AudienceRoute />
        </LocalePrefixGate>
      }
    />
    <Route
      path="/:locale/para/:audienceSegment"
      element={
        <LocalePrefixGate>
          <AudienceRoute />
        </LocalePrefixGate>
      }
    />
    <Route
      path="/:locale/blog"
      element={
        <LocalePrefixGate>
          <Posts />
        </LocalePrefixGate>
      }
    />
    <Route
      path="/:locale/blog/:slug"
      element={
        <LocalePrefixGate>
          <BlogPost />
        </LocalePrefixGate>
      }
    />
    <Route
      path="/:locale/terms-privacy"
      element={
        <LocalePrefixGate>
          <TermsAndPrivacy />
        </LocalePrefixGate>
      }
    />
    <Route
      path="/:locale/pricing"
      element={
        <LocalePrefixGate>
          <Pricing />
        </LocalePrefixGate>
      }
    />
    <Route
      path="/:locale/precios"
      element={
        <LocalePrefixGate>
          <Pricing />
        </LocalePrefixGate>
      }
    />
    <Route
      path="/:locale/markets/:country"
      element={
        <LocalePrefixGate>
          <CountryHubPage />
        </LocalePrefixGate>
      }
    />
    <Route
      path="/:locale/markets/:country/:city"
      element={
        <LocalePrefixGate>
          <CitySeoPage />
        </LocalePrefixGate>
      }
    />
  </Routes>
);
